import React, { useState, useEffect } from 'react';
import { analyticsService } from '../../services/analytics.service';
import { KpiQueryParams } from '../../types/kpi.types';
import { MetricCard, LineChart } from '../Charts';
import { Loader } from '../Loader';

interface MainMonthlyOverviewProps {
  queryParams: KpiQueryParams;
}

const formatMonthLabel = (month: string): string => {
  const [year, m] = month.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(m, 10) - 1]} ${year}`;
};

const formatReasonType = (reason: string): string => {
  if (!reason || !reason.trim()) return '(empty)';
  try {
    const parsed = JSON.parse(reason);
    const type = parsed?.reasonType;
    if (typeof type === 'string' && type) {
      return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
    }
  } catch {
    // Not JSON, return as-is
  }
  return reason;
};

export const MainMonthlyOverview: React.FC<MainMonthlyOverviewProps> = ({
  queryParams,
}) => {
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [metrics, setMetrics] = useState<any>(null);
  const [dailyAmounts, setDailyAmounts] = useState<Array<{ day: number; date: string; amount: number }>>([]);
  const [transferReasons, setTransferReasons] = useState<Array<{ reason: string; amount: number; percentage: number }>>([]);
  const [monthsLoading, setMonthsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadMonths = async () => {
      try {
        setMonthsLoading(true);
        setError(null);
        const months = await analyticsService.getMainMonthlyAvailableMonths({
          companyId: queryParams.companyId,
          database: queryParams.database,
        });
        setAvailableMonths(months);
        if (months.length > 0) {
          setSelectedMonth(months[0]);
        } else {
          setSelectedMonth('');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load available months');
      } finally {
        setMonthsLoading(false);
      }
    };

    loadMonths();
  }, [queryParams.companyId, queryParams.database]);

  useEffect(() => {
    if (!selectedMonth) return;

    const loadMetrics = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await analyticsService.getMainMonthlyMetrics({
          month: selectedMonth,
          companyId: queryParams.companyId,
          database: queryParams.database,
        });
        setMetrics(data.metrics);
        setDailyAmounts(data.dailyAmounts || []);
        setTransferReasons(data.transferReasons || []);
      } catch (err: any) {
        setError(err.message || 'Failed to load metrics');
      } finally {
        setLoading(false);
      }
    };

    loadMetrics();
  }, [selectedMonth, queryParams.companyId, queryParams.database]);

  const today = new Date();
  const [year, monthNum] = selectedMonth.split('-').map(Number);
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() + 1 === monthNum;
  const prevMonthNum = monthNum === 1 ? 12 : monthNum - 1;
  const prevMonthYear = monthNum === 1 ? year - 1 : year;
  const prevMonthLabel = `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][prevMonthNum - 1]} ${prevMonthYear}`;

  const formatComparison = (
    current: number,
    prev: number | null | undefined,
    formatter: (n: number) => string,
  ): React.ReactNode => {
    const prevVal = prev ?? 0;
    const pct = prevVal !== 0 ? ((current - prevVal) / prevVal) * 100 : null;
    return (
      <>
        <div style={{ fontSize: '12px', color: '#999' }}>
          {prevMonthLabel}: {formatter(prevVal)}
        </div>
        {pct !== null && (
          <div
            style={{
              fontSize: '12px',
              color: pct >= 0 ? '#00C851' : '#ff4444',
              marginTop: '2px',
            }}
          >
            {pct >= 0 ? '+' : ''}{pct.toFixed(1)}%
          </div>
        )}
      </>
    );
  };

  const lastDay = new Date(year, monthNum, 0).getDate();
  const amountsByDay = new Map(dailyAmounts.map((d) => [d.day, d.amount]));
  const chartData = Array.from({ length: lastDay }, (_, i) => {
    const day = i + 1;
    return {
      date: `${day}`,
      value: amountsByDay.get(day) ?? 0,
    };
  });

  if (monthsLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#ff4444', backgroundColor: '#fff', borderRadius: '12px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
        <div style={{ fontSize: '18px', fontWeight: 600 }}>Error Loading Data</div>
        <div style={{ fontSize: '14px', marginTop: '8px' }}>{error}</div>
      </div>
    );
  }

  if (availableMonths.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#666', backgroundColor: '#fff', borderRadius: '12px' }}>
        <div style={{ fontSize: '18px' }}>No transfer data available for the selected filters.</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#333' }}>
          📅 Main Monthly Overview
        </h2>
        <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '14px' }}>
          Key metrics for the selected month
        </p>
      </div>

      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <label style={{ fontWeight: 600, color: '#333' }}>Select month:</label>
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          style={{
            padding: '8px 16px',
            borderRadius: '4px',
            border: '1px solid #ddd',
            fontSize: '14px',
            minWidth: '180px',
            cursor: 'pointer',
            backgroundColor: '#fff',
          }}
        >
          {availableMonths.map((m) => (
            <option key={m} value={m}>
              {formatMonthLabel(m)}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <Loader />
      ) : metrics ? (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px',
              marginBottom: '30px',
            }}
          >
            {isCurrentMonth && (
              <MetricCard
                title="Transfer Amount Today"
                value={`₪${(metrics.transferAmountToday ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
                subtitle="Total transfers today"
                color="#4CAF50"
              />
            )}
            <MetricCard
              title="Transfer Amount Monthly"
              value={`₪${metrics.transferAmountMonthly.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
              subtitle={formatComparison(
                metrics.transferAmountMonthly,
                metrics.transferAmountLastMonth,
                (n) => `₪${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
              )}
              color="#2196F3"
            />
            <MetricCard
              title="Avg Amount per Transfer"
              value={`₪${metrics.avgAmountPerTransferMonthly.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              subtitle={formatComparison(
                metrics.avgAmountPerTransferMonthly,
                metrics.avgAmountPerTransferLastMonth,
                (n) => `₪${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              )}
              color="#ffc658"
            />
            <MetricCard
              title="Unique Users Who Transferred"
              value={metrics.uniqueUsersTransferred.toLocaleString()}
              subtitle={formatComparison(
                metrics.uniqueUsersTransferred,
                metrics.uniqueUsersTransferredLastMonth,
                (n) => n.toLocaleString(),
              )}
              color="#ff9800"
            />
            <MetricCard
              title="Total Transfers"
              value={metrics.totalTransfersMonthly.toLocaleString()}
              subtitle={formatComparison(
                metrics.totalTransfersMonthly,
                metrics.totalTransfersLastMonth,
                (n) => n.toLocaleString(),
              )}
              color="#e91e63"
            />
            <MetricCard
              title="Total Users (Registered in app)"
              value={(metrics.totalUsers ?? 0).toLocaleString()}
              subtitle={formatComparison(
                metrics.totalUsers ?? 0,
                metrics.totalUsersLastMonth,
                (n) => n.toLocaleString(),
              )}
              color="#607D8B"
            />
            <MetricCard
              title="Total App Entrances"
              value={(metrics.totalAppEntrances ?? 0).toLocaleString()}
              subtitle={formatComparison(
                metrics.totalAppEntrances ?? 0,
                metrics.totalAppEntrancesLastMonth,
                (n) => n.toLocaleString(),
              )}
              color="#009688"
            />
          </div>

          <div style={{ marginTop: '30px', marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: 600, color: '#333' }}>
              Transfer Reasons (by Amount)
            </h3>
            <p style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
              Successful transfers with userAddedInfo in {formatMonthLabel(selectedMonth)}
            </p>
            {transferReasons.length > 0 ? (
              <div style={{ overflowX: 'auto', marginBottom: '24px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #ddd', textAlign: 'left' }}>
                      <th style={{ padding: '8px 12px' }}>Reason</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right' }}>Amount (₪)</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right' }}>%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transferReasons.map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '8px 12px', maxWidth: '200px' }} title={r.reason}>
                          {formatReasonType(r.reason)}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                          {r.amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>{r.percentage}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding: '24px', backgroundColor: '#f9f9f9', borderRadius: '8px', color: '#666', fontSize: '14px' }}>
                No transfer reason data for this month. Transfer reasons appear when users provide info at transfer time.
              </div>
            )}
          </div>

          <div style={{ marginTop: '30px' }}>
            <LineChart
              data={chartData}
              title={`Daily Transfer Amounts - ${formatMonthLabel(selectedMonth)}`}
              color="#4CAF50"
              height={350}
              tickFormatter={(v) => v >= 1000 ? `₪${(v / 1000).toFixed(1)}k` : `₪${Math.round(v)}`}
            />
          </div>
        </>
      ) : null}
    </div>
  );
};
